import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../../utils';

// Wrapper for Dialog that prevents closing when clicking on context menu
const Dialog = ({
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) => {
  // Track if context menu click is happening
  const contextMenuClickRef = React.useRef(false);

  // Track clicks to detect context menu - use capture phase at highest priority
  React.useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-bug-report-context-menu]')) {
        // Set flag immediately to prevent dialog from closing
        (window as any).__contextMenuClicked = true;
        contextMenuClickRef.current = true;
        // Clear flag after event processing
        setTimeout(() => {
          (window as any).__contextMenuClicked = false;
          contextMenuClickRef.current = false;
        }, 200);
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-bug-report-context-menu]')) {
        (window as any).__contextMenuClicked = true;
        contextMenuClickRef.current = true;
        setTimeout(() => {
          (window as any).__contextMenuClicked = false;
          contextMenuClickRef.current = false;
        }, 200);
      }
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-bug-report-context-menu]')) {
        (window as any).__contextMenuClicked = true;
        contextMenuClickRef.current = true;
        setTimeout(() => {
          (window as any).__contextMenuClicked = false;
          contextMenuClickRef.current = false;
        }, 200);
      }
    };

    // Use capture phase at the highest priority to catch events before Radix UI
    document.addEventListener('pointerdown', handlePointerDown, {
      capture: true,
      passive: false,
    });
    document.addEventListener('mousedown', handleMouseDown, {
      capture: true,
      passive: false,
    });
    document.addEventListener('click', handleClick, {
      capture: true,
      passive: false,
    });

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, {
        capture: true,
      } as any);
      document.removeEventListener('mousedown', handleMouseDown, {
        capture: true,
      } as any);
      document.removeEventListener('click', handleClick, {
        capture: true,
      } as any);
    };
  }, []);

  const handleOpenChange = (open: boolean) => {
    // If dialog is being closed, check if the click was on context menu
    if (!open) {
      // Check global flag first (most reliable)
      if ((window as any).__contextMenuClicked) {
        (window as any).__contextMenuClicked = false;
        // Don't close dialog
        return;
      }

      // Check if context menu is currently visible and was clicked
      const contextMenu = document.querySelector(
        '[data-bug-report-context-menu]'
      ) as HTMLElement;
      if (contextMenu && contextMenu.offsetParent !== null) {
        // Context menu is visible, check if it was clicked
        if (contextMenuClickRef.current) {
          // Click was on context menu, don't close dialog
          return;
        }
      }

      // Also check the last click target as fallback
      const lastClickTarget = (window as any).__lastClickTarget;
      if (lastClickTarget && lastClickTarget instanceof HTMLElement) {
        if (lastClickTarget.closest('[data-bug-report-context-menu]')) {
          // Click was on context menu, don't close dialog
          return;
        }
      }

      // Restore body pointer-events when dialog closes
      // Use setTimeout to ensure it happens after Radix UI's cleanup
      setTimeout(() => {
        // Check if there are any other open dialogs
        const openDialogs = document.querySelectorAll('[role="dialog"][data-state="open"]');
        if (openDialogs.length === 0) {
          // No open dialogs, restore body pointer-events
          if (document.body.style.pointerEvents === 'none') {
            document.body.style.pointerEvents = '';
          }
        }
      }, 0);
    }

    // Otherwise, proceed with the change
    onOpenChange?.(open);
  };

  return <DialogPrimitive.Root onOpenChange={handleOpenChange} {...props} />;
};
Dialog.displayName = 'Dialog';

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, onClick, onPointerDown, ...props }, ref) => {
  const overlayRef = React.useRef<HTMLDivElement | null>(null);

  // Check if context menu or select dropdown is visible and disable pointer events on overlay if needed
  React.useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const checkOverlays = () => {
      // First check if the overlay itself is closed (data-state="closed")
      // If it's closed, we should disable pointer events immediately
      const isOverlayClosed = overlay.getAttribute('data-state') === 'closed';
      if (isOverlayClosed) {
        overlay.style.pointerEvents = 'none';
        return;
      }

      const contextMenu = document.querySelector(
        '[data-bug-report-context-menu]'
      ) as HTMLElement;

      // Check for Select content - use our custom data attribute
      const selectContent = document.querySelector(
        '[data-select-content="true"]'
      ) as HTMLElement;

      // Check for Popover content - use our custom data attribute
      const popoverContent = document.querySelector(
        '[data-popover-content="true"]'
      ) as HTMLElement;

      // Also check for any element with data-state="open" and high z-index (Radix UI components)
      const openRadixComponents = Array.from(
        document.querySelectorAll('[data-state="open"]')
      ).filter((el) => {
        const zIndex = window.getComputedStyle(el as HTMLElement).zIndex;
        return (
          zIndex && parseInt(zIndex) > 50 && !el.closest('[role="dialog"]')
        );
      }) as HTMLElement[];

      const hasContextMenu = contextMenu && contextMenu.offsetParent !== null;
      const hasSelect = selectContent && selectContent.offsetParent !== null;
      const hasPopover = popoverContent && popoverContent.offsetParent !== null;
      const hasOpenRadix = openRadixComponents.some(
        (el) => el.offsetParent !== null
      );

      if (hasContextMenu || hasSelect || hasPopover || hasOpenRadix) {
        // Context menu, select, or popover is visible, disable pointer events on overlay
        overlay.style.pointerEvents = 'none';
      } else {
        // No overlays visible, restore pointer events only if overlay is open
        if (!isOverlayClosed) {
          overlay.style.pointerEvents = 'auto';
        }
      }
    };

    // Check immediately
    checkOverlays();

    // Use MutationObserver to watch for Select/Popover opening/closing and overlay state changes
    const observer = new MutationObserver((mutations) => {
      // Check if overlay state changed
      const overlayStateChanged = mutations.some((mutation) => {
        if (mutation.target === overlay || overlay.contains(mutation.target as Node)) {
          return mutation.type === 'attributes' && mutation.attributeName === 'data-state';
        }
        return false;
      });
      
      if (overlayStateChanged) {
        // Overlay state changed, check immediately
        checkOverlays();
      } else {
        // Other changes, check overlays
        checkOverlays();
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'data-state',
        'data-select-content',
        'data-popover-content',
        'data-radix-select-viewport',
      ],
    });

    // Also observe the overlay itself for state changes
    if (overlay) {
      observer.observe(overlay, {
        attributes: true,
        attributeFilter: ['data-state'],
      });
    }

    // Also check periodically as fallback
    const interval = setInterval(checkOverlays, 50);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // Combine refs
  const combinedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      overlayRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref]
  );

  return (
    <DialogPrimitive.Overlay
      ref={combinedRef}
      className={cn(
        'fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className
      )}
      style={{ pointerEvents: 'auto' }}
      onClick={(e) => {
        // Get the actual element that was clicked (not the overlay)
        const target = e.target as HTMLElement;

        // If the click is directly on the overlay (not a child), close the dialog
        if (target === overlayRef.current || target === e.currentTarget) {
          // Check if there's a Select or Popover open - if so, don't close
          const selectContent = document.querySelector(
            '[data-select-content="true"]'
          ) as HTMLElement;
          const popoverContent = document.querySelector(
            '[data-popover-content="true"]'
          ) as HTMLElement;

          if (
            (selectContent && selectContent.offsetParent !== null) ||
            (popoverContent && popoverContent.offsetParent !== null)
          ) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          onClick?.(e);
          return;
        }

        // Don't close dialog if clicking on context menu, select, or popover
        const contextMenu = document.querySelector(
          '[data-bug-report-context-menu]'
        ) as HTMLElement;

        // Check if click is on context menu
        if (contextMenu && contextMenu.offsetParent !== null) {
          const rect = contextMenu.getBoundingClientRect();
          const clickX = e.clientX;
          const clickY = e.clientY;

          if (
            clickX >= rect.left &&
            clickX <= rect.right &&
            clickY >= rect.top &&
            clickY <= rect.bottom
          ) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }

        // Check if click is on Select or Popover content by checking the actual clicked element
        const clickedElement = document.elementFromPoint(
          e.clientX,
          e.clientY
        ) as HTMLElement;
        if (clickedElement) {
          // Check if clicked element is within a Select viewport or Popover content
          const isInSelect =
            clickedElement.closest('[data-select-content="true"]') ||
            clickedElement.closest('[data-radix-select-viewport]');
          const isInPopover = clickedElement.closest(
            '[data-popover-content="true"]'
          );

          if (isInSelect || isInPopover) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }

        // Also check if target is within context menu
        if (target.closest('[data-bug-report-context-menu]')) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        onClick?.(e);
      }}
      onPointerDown={(e) => {
        // Allow pointer events to pass through to context menu, select, or popover
        const target = e.target as HTMLElement;

        // If pointer down is on Select or Popover, don't let overlay handle it
        const clickedElement = document.elementFromPoint(
          e.clientX,
          e.clientY
        ) as HTMLElement;
        if (clickedElement) {
          const isInSelect =
            clickedElement.closest('[data-select-content="true"]') ||
            clickedElement.closest('[data-radix-select-viewport]');
          const isInPopover = clickedElement.closest(
            '[data-popover-content="true"]'
          );

          if (isInSelect || isInPopover) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }

        if (target.closest('[data-bug-report-context-menu]')) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onPointerDown?.(e);
      }}
      {...props}
    />
  );
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onInteractOutside, style, ...props }, ref) => {
  // Ensure body pointer-events are restored when dialog closes
  React.useEffect(() => {
    const checkBodyPointerEvents = () => {
      // Check if there are any open dialogs
      const openDialogs = document.querySelectorAll('[role="dialog"][data-state="open"]');
      
      if (openDialogs.length === 0) {
        // No open dialogs, ensure body pointer-events are restored
        if (document.body.style.pointerEvents === 'none') {
          document.body.style.pointerEvents = '';
        }
      }
    };

    // Check immediately
    checkBodyPointerEvents();

    // Watch for dialog state changes
    const observer = new MutationObserver(() => {
      checkBodyPointerEvents();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });

    // Also check periodically as fallback
    const interval = setInterval(checkBodyPointerEvents, 100);

    return () => {
      observer.disconnect();
      clearInterval(interval);
      // Cleanup: ensure body pointer-events are restored on unmount
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    };
  }, []);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-[51] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
          className
        )}
        style={{ pointerEvents: 'auto', ...style }}
        onInteractOutside={(e) => {
        // Prevent dialog from closing when clicking on context menu
        // Check global flag first (set by context menu click handlers)
        if ((window as any).__contextMenuClicked) {
          (window as any).__contextMenuClicked = false;
          e.preventDefault();
          e.stopPropagation();
          // Don't call original handler - we're preventing the close
          return;
        }

        // Check if context menu is visible
        const contextMenu = document.querySelector(
          '[data-bug-report-context-menu]'
        ) as HTMLElement;

        if (contextMenu && contextMenu.offsetParent !== null) {
          // Context menu is visible, prevent dialog from closing
          // We'll be conservative and prevent close if context menu is visible
          e.preventDefault();
          e.stopPropagation();
          // Don't call original handler - we're preventing the close
          return;
        }

        // Also check the target element
        const target = e.target as HTMLElement;
        if (target?.closest('[data-bug-report-context-menu]')) {
          e.preventDefault();
          e.stopPropagation();
          // Don't call original handler - we're preventing the close
          return;
        }

        // Only call original handler if we're not preventing the close
        onInteractOutside?.(e);
      }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
